import React, { useState, useEffect, useRef } from 'react';
import { getBackendURL } from '../utils';
import { useNavigate } from 'react-router-dom';
import ProgressTabs from '../components/ProgressTabs';
import SweatTextTitle from '../steps/SweatTextTitle';
import RemindersInfo from './RemindersInfo';

import { useAuth } from '../auth';
import '../BasicReminder.css';

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const weekdayTimeslotFieldNames = [
  'timeslot_sunday',
  'timeslot_monday',
  'timeslot_tuesday',
  'timeslot_wednesday',
  'timeslot_thursday',
  'timeslot_friday',
  'timeslot_saturday'

]

const defaultTime = '10:00';

const defaultClient = {
  name: '',
  pre_user_email: '',
  consent_timestamp: false,
  timeslot_sunday: null,
  timeslot_monday: null,
  timeslot_tuesday: null,
  timeslot_wednesday: null,
  timeslot_thursday: null,
  timeslot_friday: null,
  timeslot_saturday: null
};

const Step4 = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([{ ...defaultClient }]); // Add consent_timestamp field
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false); // New state for syncing status
  const [synced, setSynced] = useState(false); // New state for synced status
  const [syncError, setSyncError] = useState(false); // New state for sync error
  const debounceTimeout = useRef(null); // Add debounce timeout ref
  const navigate = useNavigate();

  const steps = ['Step 1', 'Step 2', 'Step 3', 'Step 4', 'Step 5', 'Step 6'];

  useEffect(() => {
    if (!user) return;
    if (!user || !user.role.includes('trainer')) {
      alert('You must be a trainer to access this step. If you are indeed a trainer, please contact our Instagram for account support.');
      navigate('/tools/reminders/step1');
      return;
    }

    const fetchClients = async () => {
      try {
        const res = await fetch(getBackendURL() + `/client?trainer_info_id=${user.trainer_info.id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          }
        });

        if (!res.ok) throw new Error('Failed to fetch clients');
        const data = await res.json();
        setClients(data || []);
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user.trainer_info) {
      fetchClients();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleClientsChange = (updatedClients) => {
    setSyncing(true);
    setSynced(false);
    setSyncError(false);
    setClients(updatedClients);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      autosyncClients(updatedClients);
    }, 1000);
  };

  const autosyncClients = async (updatedClients) => {
    try {
      for (const client of updatedClients) {
        if (client.id) {
          // Fetch the original client data
          const res = await fetch(getBackendURL() + `/client/${client.id}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
            }
          });

          if (!res.ok) throw new Error('Failed to fetch original client data');
          const originalClient = await res.json();

          // Determine fields that have changed
          const changedFields = {};
          for (const key in client) {
            if (client[key] !== originalClient[key]) {
              changedFields[key] = client[key];
            }
          }

          // Only send update if there are changes
          if (Object.keys(changedFields).length > 0) {
            await fetch(getBackendURL() + `/client/${client.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
              },
              body: JSON.stringify(changedFields)
            });
          }
        }
      }
      setSynced(true);
    } catch (err) {
      console.error('Error autosyncing clients:', err);
      setSyncError(true);
    } finally {
      setSyncing(false);
    }
  };

  const inviteClientWithEmail = async (index) => {
    const client = clients[index];
    if (!client.pre_user_name) {
      alert('Please enter a name for the client before saving and inviting.');
      return;
    }
    if (!client.pre_user_email) {
      alert('Please enter an email address before saving and inviting.');
      return;
    }

    try {
      // Send invitation email
      await fetch(getBackendURL() + '/api/invite-client', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
        },
        body: JSON.stringify({ email: client.pre_user_email,
          name: client.pre_user_name
         })
      });

      alert('Invitation email sent successfully!');
    } catch (err) {
      console.error('Error inviting client:', err);
      alert('Failed to invite client.');
    }
  };

  const updateClient = (index, field, value) => {
    const updatedClients = [...clients];
    updatedClients[index][field] = value || null;
    handleClientsChange(updatedClients);
  };

  const removeClient = async (index) => {
    const client = clients[index];
    if (client.id) {
      try {
        await fetch(getBackendURL() + `/client/${client.id}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          }
        });
      } catch (err) {
        console.error('Error deleting client:', err);
        alert('Failed to delete client.');
        return;
      }
    }
    const updatedClients = clients.filter((_, i) => i !== index);
    setClients(updatedClients);
    setSynced(false);
  };

  return (
    <div className="reminder-container fogged-card">
      <SweatTextTitle />
      <RemindersInfo />

      <ProgressTabs currentStep={3} />

      
      <h2 className="step-title">Step 4: Specify when each client gets a reminder</h2>

      {/* Clients: {JSON.stringify(clients, null, 2)} */}
      {loading ? (
        <div className="loading-indicator" style={{ textAlign: 'center', marginTop: '2rem' }}>
          <span className="spinner" style={{
            width: '2rem',
            height: '2rem',
            border: '3px solid #ccc',
            borderTop: '3px solid #000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}></span>
          <p>Loading...</p>
        </div>
      ) : (
        clients.map((client, index) => (
          <div key={index} className="client-card ptc-card">
            <h2>Client #{index + 1}</h2>
            <p>
              <label>
                Name:
                <input
                  type="text"
                  value={client.pre_user_name}
                  onChange={(e) => updateClient(index, 'pre_user_name', e.target.value)}
                />
              </label>
            </p>

            <p>
              <label>
                Email:
                <input
                  type="email"
                  value={client.pre_user_email}
                  onChange={(e) => updateClient(index, 'pre_user_email', e.target.value)}
                />
              </label>
              <button
              className="ptc-button"
                onClick={() => inviteClientWithEmail(index)}
                style={{ marginLeft: '1rem' }}
                disabled={loading}
              >
                Send Client Invite Email
              </button>
            </p>

            <p>
              Signup Status: {' '}
              <input type="text" disabled value={client.consent_timestamp ? "Accepted" : "Not accepted yet"} />
            </p>

            <div className="reminders-appointments-days-list">
              <b>Days/Times This Client has Appointments With You:</b>
              
              <p>
                The client will receive a reminder approximately 1 hour before 
                the appointment. Configuring this time buffer is not possible yet 
                but is planned as a future feature.
              </p>
              {weekdays.map((day, dayIndex) => (
                <div key={dayIndex} className="schedule-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!client[`timeslot_${day.toLowerCase()}`]}
                      onChange={(e) =>
                        updateClient(index, `timeslot_${day.toLowerCase()}`, e.target.checked ? defaultTime : null)
                      }
                    />
                    {day}
                  </label>
                  {client[`timeslot_${day.toLowerCase()}`] && (
                    <input
                      type="time"
                      value={client[`timeslot_${day.toLowerCase()}`] || ''}
                      onChange={(e) =>
                        updateClient(index, `timeslot_${day.toLowerCase()}`, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}
            </div>
            <button
              className="ptc-button"
              onClick={() => removeClient(index)}
              style={{ marginBottom: '1rem', background: '#f44336', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px' }}
              disabled={loading}
            >
              Remove Client
            </button>
          </div>
        ))
      )}
      <button
        className="ptc-button "
      onClick={() => handleClientsChange([...clients, { ...defaultClient }])} disabled={loading}>
        + Add Client
      </button>

      {syncing ? (
        <span className="syncing-indicator" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '1rem' }}>
          <span className="spinner" style={{
            width: '1rem',
            height: '1rem',
            border: '2px solid #ccc',
            borderTop: '2px solid #000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '0.5rem',
          }}></span>
          Syncing...
        </span>
      ) : synced ? (
        <span style={{ color: 'green', fontWeight: 'bold', marginLeft: '1rem' }}>✔ Synced</span>
      ) : syncError ? (
        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '1rem' }}>✘ Failed</span>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button className="ptc-secondary-button" onClick={() => navigate('/tools/reminders/step3')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Previous Step
        </button>
        <button className="ptc-button" onClick={() => navigate('/tools/reminders/step5')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Next Step
        </button>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Step4;
